import { Box } from "@mui/material";
import {
  StatCard,
  TwoLineListSection,
  DashboardHeader,
} from "components/dashboard";
import DashboardCharts from "components/dashboard/DashboardCharts";
import DashboardLayout from "components/layouts/DashboardLayout";
import { getPriorityColor } from "helpers/getPriorityColor";
import React, { useState } from "react";

const ProviderDashboard: React.FC = () => {
  const [selectedRole, setSelectedRole] = useState<string>("doctor");

  // Mock data for demonstration
  const statsData = [
    {
      title: "Appointment",
      value: 3,
      change: -12.15,
      changeText: "Since last week",
      actionText: "View Appointment",
    },
    {
      title: "Report",
      value: 5,
      change: 25.56,
      changeText: "Since last week",
      actionText: "View Report",
    },
    {
      title: "Patient",
      value: 7,
      change: -12.15,
      changeText: "Since last week",
      actionText: "View Patient",
    },
    {
      title: "Task",
      value: 5,
      change: 65.14,
      changeText: "Since last week",
      actionText: "View Task",
    },
  ];

  const upcomingAppointments = [
    {
      patientName: "Emma Thompson",
      time: "9:00 AM",
      duration: "30 mins",
    },
    {
      patientName: "Michael Chen",
      time: "9:45 AM",
      duration: "45 mins",
    },
    {
      patientName: "Sophia Rodriguez",
      time: "10:45 AM",
      duration: "45 mins",
    },
    {
      patientName: "Olivia Parker",
      time: "1:45 PM",
      duration: "30 mins",
    },
    {
      patientName: "James Wilson",
      time: "3:00 PM",
      duration: "45 mins",
    },
  ];

  const pendingTasks = [
    {
      title: "Review lab results",
      time: "9:00 AM",
      priority: "Low" as const,
    },
    {
      title: "Complete medical certificate",
      time: "9:45 AM",
      priority: "High" as const,
    },
    {
      title: "Review Treatment plan",
      time: "10:45 AM",
      priority: "High" as const,
    },
    {
      title: "Sign document",
      time: "1:45 PM",
      priority: "Low" as const,
    },
    {
      title: "Sign document",
      time: "3:00 PM",
      priority: "Medium" as const,
    },
  ];

  const patientNotes = [
    {
      patientName: "Emma Thompson",
      note: "Patient presented with persistent headaches for 2 weeks. Prescribed sumatriptan 50mg and recommended follow-up in 2 weeks if symptoms persist.",
    },
    {
      patientName: "Michael Chen",
      note: "Follow-up for hypertension. BP readings have improved (135/85). Continuing current medication regimen. Advised on dietary modifications.",
    },
    {
      patientName: "Sophia Rodriguez",
      note: "Chest pain evaluation. ECG normal. Ordered stress test and lipid panel. Patient to return next week for results discussion.",
    },
  ];

  const handleRoleChange = (role: string) => {
    setSelectedRole(role);
  };

  return (
    <DashboardLayout title="Dashboard">
      <Box>
        <DashboardHeader onRoleChange={handleRoleChange} />

        {/* Show dashboard components only when NOT admin */}
        {selectedRole !== "admin" && (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: "10px",
                marginBottom: "8px",
              }}
            >
              {statsData.map((stat) => (
                <StatCard
                  key={stat.title}
                  title={stat.title}
                  value={stat.value}
                  change={stat.change}
                  changeText={stat.changeText}
                  actionText={stat.actionText}
                  onActionClick={() => console.log(`${stat.title} clicked`)}
                />
              ))}
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: "10px",
                marginBottom: "8px",
              }}
            >
              <TwoLineListSection
                title="Upcoming Appointments"
                items={upcomingAppointments.map((a) => ({
                  subtitle: a.patientName,
                  content: `${a.time} - ${a.duration}`,
                }))}
                height="310px"
              />

              <TwoLineListSection
                title="Pending Tasks"
                items={pendingTasks.map((t) => ({
                  subtitle: t.title,
                  content: (
                    <>
                      {t.time} -{" "}
                      <span style={{ color: getPriorityColor(t.priority) }}>
                        {t.priority}
                      </span>
                    </>
                  ),
                }))}
                height="310px"
              />
            </Box>

            <Box>
              <TwoLineListSection
                title="Recent Patient Notes"
                items={patientNotes.map((a) => ({
                  subtitle: a.patientName,
                  content: a.note,
                }))}
              />
            </Box>
          </>
        )}

        {/* Show charts only when Admin is selected */}
        {selectedRole === "admin" && <DashboardCharts />}
      </Box>
    </DashboardLayout>
  );
};

export default ProviderDashboard;
